/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/users.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useSyncExternalStore } from 'react';

/**
 * Who is acting (ADR-030). With sign-in, it is the person of the session: the server takes it from the session
 * cookie and writes it into approvals, locks and the audit trail. The public demo (KONTOR_PUBLIC_DEMO=1) has no
 * sign-in: there the person approving picks one of three fictitious people, sent as `x-kontor-user`.
 */

export interface Person { id: string; name: string; title?: string | null; role: string }
/** Roles (server/src/permissions.ts): approver = Geschäftsführung/Verwaltungsrat who approve, client = the Mandant's own people. */
export type Role = 'admin' | 'accountant' | 'approver' | 'client' | 'viewer';
export type LoginMethod = 'password' | 'email_code';
export interface Me { id: string; email: string; name: string; title: string | null; role: Role; tenants: '*' | string[]; mfa: boolean; loginMethod: LoginMethod }
export interface AuthState {
  mode: 'auth' | 'public-demo'; needsSetup: boolean; user: Me | null; mfaPending: boolean; enrolRequired: boolean;
  requireMfa?: boolean; demoMode: boolean;
  /** An e-mail provider is set up: people can sign in with a code by e-mail (ADR-033). */
  emailLogin?: boolean;
}

import { getLang, type Lang } from './i18n';

const LOGIN_LABEL_DE: Record<LoginMethod, string> = { email_code: 'Code per E-Mail', password: 'Passwort' };
const LOGIN_LABEL_EN: Record<LoginMethod, string> = { email_code: 'Code by email', password: 'Password' };
export const loginLabel = (method: LoginMethod, lang: Lang = getLang()) => (lang === 'en' ? LOGIN_LABEL_EN[method] : LOGIN_LABEL_DE[method]) ?? LOGIN_LABEL_DE[method];
export const LOGIN_LABEL: Record<LoginMethod, string> = new Proxy(LOGIN_LABEL_DE, {
  get(target, prop: LoginMethod) {
    return loginLabel(prop, getLang()) ?? target[prop];
  },
});

const ROLE_LABEL_DE: Record<Role, string> = { admin: 'Admin', accountant: 'Buchhaltung', approver: 'Freigabe', client: 'Kunde', viewer: 'Nur lesen' };
const ROLE_LABEL_EN: Record<Role, string> = { admin: 'Admin', accountant: 'Accounting', approver: 'Approver', client: 'Client', viewer: 'Read-only' };
export const roleLabel = (role: Role, lang: Lang = getLang()) => (lang === 'en' ? ROLE_LABEL_EN[role] : ROLE_LABEL_DE[role]) ?? ROLE_LABEL_DE[role];
export const ROLE_LABEL: Record<Role, string> = new Proxy(ROLE_LABEL_DE, {
  get(target, prop: Role) {
    return roleLabel(prop, getLang()) ?? target[prop];
  },
});


/** The public demo's people. */
export const PERSONAS: Person[] = [
  { id: 'user:anna.keller', name: 'Anna Keller', title: 'Treuhand (OBT)', role: 'accountant' },
  { id: 'user:marco.steiner', name: 'Marco Steiner', title: 'Geschäftsführung', role: 'accountant' },
  { id: 'user:ruth.baumann', name: 'Ruth Baumann', title: 'Verwaltungsrat', role: 'accountant' },
];

let auth: AuthState | null = null;
let directory: Person[] = PERSONAS;
const KEY = 'kontor-user';
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

export const isPublicDemo = () => auth?.mode === 'public-demo';
export const getAuth = () => auth;

export function setAuth(a: AuthState) {
  auth = a;
  emit();
  if (a.user || a.mode === 'public-demo') void loadDirectory();
}

/** Names for the ids in journals and trails. */
export async function loadDirectory() {
  try {
    const r = await fetch('/api/users/directory');
    if (r.ok) { directory = await r.json(); emit(); }
  } catch { /* offline: ids are shown instead of names */ }
}

export const useAuth = () => useSyncExternalStore(subscribe, () => auth);
export const usePeople = () => useSyncExternalStore(subscribe, () => directory);
export function useMe() { return useAuth()?.user ?? null; }

function persona(): string {
  try { return localStorage.getItem(KEY) || PERSONAS[0].id; } catch { return PERSONAS[0].id; }
}

/** The acting person's id: the signed-in user, or the picked persona in the public demo. */
export function currentUserId(): string {
  return !isPublicDemo() && auth?.user ? auth.user.id : persona();
}

/** Public demo only: pick the person who acts. */
export function setCurrentUser(id: string) {
  try { localStorage.setItem(KEY, id); } catch { /* private mode: stays for this page view */ }
  syncCookie(id);
  emit();
}
/**
 * Public demo: file previews, <img>, <iframe> and download links cannot send `x-kontor-user`. The same name goes into
 * a cookie, so the access log (GeBüV Art. 8) records who opened a Beleg. With sign-in the session cookie does this.
 */
function syncCookie(id: string) {
  try { document.cookie = `${KEY}=${encodeURIComponent(id)}; path=/; SameSite=Strict`; } catch { /* no document */ }
}
syncCookie(persona());

export const userName = (id: string | null | undefined) => {
  if (!id) return '—';
  const u = directory.find((x) => x.id === id) ?? PERSONAS.find((x) => x.id === id);
  if (u) return u.name;
  if (auth?.user?.id === id) return auth.user.name;
  return id.replace(/^user:/, '').replace(/^agent:/, 'Agent ').replace(/^migration:/, 'Migration ').replace(/^system:/, 'System ').replace(/^email:/, 'E-Mail ');
};

export function useCurrentUser(): Person {
  const a = useAuth();
  const people = usePeople();
  const id = useSyncExternalStore(subscribe, currentUserId);
  if (a?.user && a.mode !== 'public-demo') return { id: a.user.id, name: a.user.name, title: a.user.title, role: a.user.role };
  return people.find((u) => u.id === id) ?? PERSONAS.find((u) => u.id === id) ?? PERSONAS[0];
}

/** Can this person book and import (admin, accountant)? The public demo lets everybody try everything. The server decides in the end. */
export function useCanWrite() {
  const a = useAuth();
  return a?.mode === 'public-demo' || (!!a?.user && (a.user.role === 'admin' || a.user.role === 'accountant'));
}
/** Can this person download exports (admin, accountant, viewer; permissions.ts 'export')? The server decides in the end. */
export function useCanExport() {
  const a = useAuth();
  return a?.mode === 'public-demo' || (!!a?.user && (a.user.role === 'admin' || a.user.role === 'accountant' || a.user.role === 'viewer'));
}
/** Can this person give a four-eyes approval (admin, accountant, approver; permissions.ts 'approve')? The server decides in the end. */
export function useCanApprove() {
  const a = useAuth();
  return a?.mode === 'public-demo' || (!!a?.user && (a.user.role === 'admin' || a.user.role === 'accountant' || a.user.role === 'approver'));
}
export function useIsAdmin() {
  const a = useAuth();
  return a?.mode === 'auth' && a.user?.role === 'admin';
}
